import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "../utils/axiosInstance";
import {
  Container, Card, Row, Col, Table, Button, Form, Modal, Badge,
  Alert, Spinner, InputGroup, Pagination, ProgressBar, ListGroup,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";

const API_URL = "/abagore_batwite";

function AbagoreBatwite() {
  // ══════════════════════════════════════════════════════════════
  // STATE MANAGEMENT
  // ══════════════════════════════════════════════════════════════
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: "", type: "" });

  // Modals
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // CRUD State
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [selectedPerson, setSelectedPerson] = useState(null);

  // Filters & Search
  const [searchTerm, setSearchTerm] = useState("");
  const [filterTrimester, setFilterTrimester] = useState("all");
  const [filterCheckup, setFilterCheckup] = useState("all");
  const [filterRisk, setFilterRisk] = useState("all");

  // UI State
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState("table");
  const [expandedCard, setExpandedCard] = useState(null);
  const [autoFillKey, setAutoFillKey] = useState(0);

  // Form State
  const [errors, setErrors] = useState({});

  const itemsPerPage = 8;

  const emptyForm = {
    amazina: "",
    imyaka: "",
    nimero_ya_telephone: "",
    aderesi: "",
    amezi_atwite: "",
    asuzumwe_kuvuzi: "Oya",
    ibibazo_byihariye: "",
    itariki_y_isuzuma: "",
    ivuriro: "",
  };

  const [form, setForm] = useState({ ...emptyForm });

  // ══════════════════════════════════════════════════════════════
  // THEME CONFIGURATION
  // ══════════════════════════════════════════════════════════════
  const theme = {
    bg: "transparent",
    surface: "rgba(22, 27, 34, 0.85)",
    surfaceLight: "rgba(30, 38, 50, 0.7)",
    text: "#e6edf3",
    textMuted: "#8b949e",
    border: "rgba(240, 246, 252, 0.08)",
    primary: "#e879f9",
    primaryDark: "#c026d3",
    primaryGlow: "rgba(232, 121, 249, 0.2)",
    secondary: "#818cf8",
    danger: "#fb7185",
    dangerGlow: "rgba(251, 113, 133, 0.15)",
    warning: "#fbbf24",
    warningGlow: "rgba(251, 191, 36, 0.15)",
    success: "#34d399",
    successGlow: "rgba(52, 211, 153, 0.15)",
    info: "#38bdf8",
    gradient: "linear-gradient(135deg, #e879f9 0%, #818cf8 100%)",
    gradientWarm: "linear-gradient(135deg, #fb7185 0%, #fbbf24 100%)",
  };

  // ══════════════════════════════════════════════════════════════
  // DATA GENERATORS
  // ══════════════════════════════════════════════════════════════
  const generateRandomData = useCallback(() => {
    const firstNames = [
      "Mukamana", "Uwimana", "Muhire", "Ingabire", "Uwase", "Mukeshimana",
      "Nyiraneza", "Umutoni", "Ishimwe", "Murekatete", "Uwamahoro", "Niyonzima",
      "Mukandayisenga", "Nyirahabimana", "Uwamariya", "Kamanzi", "Niyigena",
      "Uwingabire", "Nizeyimana", "Mukagatare", "Nyiramana", "Uwimbabazi",
    ];

    const lastNames = [
      "Alice", "Diane", "Claudine", "Marie", "Jeannette", "Grace", "Vestine",
      "Chantal", "Sandrine", "Ange", "Brigitte", "Consolée", "Odette",
      "Immaculée", "Esperance", "Justine", "Alphonsine", "Dancilla",
    ];

    const districts = [
      "Gasabo", "Kicukiro", "Nyarugenge", "Huye", "Musanze", "Rubavu",
      "Nyagatare", "Rusizi", "Karongi", "Rwamagana", "Muhanga", "Kamonyi",
    ];

    const sectors = [
      "Kimironko", "Gisozi", "Kacyiru", "Remera", "Niboye", "Gahanga",
      "Tumba", "Muhoza", "Gisenyi", "Kamembe", "Bwishyura", "Gikondo",
    ];

    const healthCenters = [
      "CS Kimironko", "CS Kacyiru", "CS Remera", "CS Gisozi", "CS Kabuga",
      "Hôpital de Kigali", "CHUK", "King Faisal Hospital", "CS Muhima",
      "CS Biryogo", "CS Nyamirambo", "Polyclinique La Medicale",
    ];

    const issues = [
      "", "", "", // Most have no issues
      "Umuvuduko w'amaraso uri hejuru",
      "Isukari mu maraso",
      "Aribwe cyane",
      "Umwana ari munsi",
      "Impinja ebyiri",
      "Yarabyaye hakiri kare mbere",
      "Afite imyaka myinshi",
      "Afite uburwayi bw'umutima",
    ];

    const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
    const randomInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

    const age = randomInt(18, 42);
    const months = randomInt(1, 9);
    const phonePrefix = ["078", "072", "073", "079"];
    const phone = pick(phonePrefix) + Math.floor(Math.random() * 10000000).toString().padStart(7, "0");

    const checked = Math.random() > 0.3 ? "Yego" : "Oya";

    // Generate a recent date for checkup
    const today = new Date();
    const checkupDate = new Date(today.getTime() - Math.random() * 60 * 24 * 60 * 60 * 1000);
    const formattedDate = checked === "Yego" ? checkupDate.toISOString().split("T")[0] : "";

    return {
      amazina: `${pick(firstNames)} ${pick(lastNames)}`,
      imyaka: age.toString(),
      nimero_ya_telephone: phone,
      aderesi: `${pick(districts)}, ${pick(sectors)}`,
      amezi_atwite: months.toString(),
      asuzumwe_kuvuzi: checked,
      ibibazo_byihariye: pick(issues),
      itariki_y_isuzuma: formattedDate,
      ivuriro: checked === "Yego" ? pick(healthCenters) : "",
    };
  }, []);

  const getUniqueRandomData = useCallback(() => {
    const existingPhones = new Set(data.map((item) => item.nimero_ya_telephone));
    let candidate;
    let attempts = 0;

    do {
      candidate = generateRandomData();
      attempts++;
    } while (existingPhones.has(candidate.nimero_ya_telephone) && attempts < 50);

    return candidate;
  }, [data, generateRandomData]);

  // ══════════════════════════════════════════════════════════════
  // STATISTICS CALCULATION
  // ══════════════════════════════════════════════════════════════
  const statistics = useMemo(() => {
    if (!data.length) return {
      total: 0, checkedUp: 0, notChecked: 0, highRisk: 0,
      dueThisMonth: 0, trimester1: 0, trimester2: 0, trimester3: 0,
      avgAge: 0, teenPregnancy: 0,
    };

    return {
      total: data.length,
      checkedUp: data.filter(d => d.asuzumwe_kuvuzi === "Yego").length,
      notChecked: data.filter(d => d.asuzumwe_kuvuzi === "Oya").length,
      highRisk: data.filter(d => d.ibibazo_byihariye?.trim()).length,
      dueThisMonth: data.filter(d => parseInt(d.amezi_atwite) >= 8).length,
      trimester1: data.filter(d => parseInt(d.amezi_atwite) <= 3).length,
      trimester2: data.filter(d => parseInt(d.amezi_atwite) >= 4 && parseInt(d.amezi_atwite) <= 6).length,
      trimester3: data.filter(d => parseInt(d.amezi_atwite) >= 7).length,
      avgAge: Math.round(data.reduce((s, d) => s + parseInt(d.imyaka || 0), 0) / data.length),
      teenPregnancy: data.filter(d => parseInt(d.imyaka) < 20).length,
    };
  }, [data]);

  // ══════════════════════════════════════════════════════════════
  // HELPER FUNCTIONS
  // ══════════════════════════════════════════════════════════════
  const getTrimester = (months) => {
    const m = parseInt(months);
    if (m <= 3) return 1;
    if (m <= 6) return 2;
    return 3;
  };

  const getTrimesterInfo = (months) => {
    const m = parseInt(months);
    if (m <= 3) return { num: 1, label: "Igihembwe 1", color: theme.info, progress: (m / 3) * 100 };
    if (m <= 6) return { num: 2, label: "Igihembwe 2", color: theme.warning, progress: ((m - 3) / 3) * 100 };
    return { num: 3, label: "Igihembwe 3", color: theme.danger, progress: ((m - 6) / 3) * 100 };
  };

  const getRiskLevel = (item) => {
    const hasIssues = item.ibibazo_byihariye?.trim();
    const notChecked = item.asuzumwe_kuvuzi === "Oya";
    const nearDue = parseInt(item.amezi_atwite) >= 8;
    const teenMom = parseInt(item.imyaka) < 18;
    const olderMom = parseInt(item.imyaka) > 35;

    if ((hasIssues && nearDue) || (notChecked && nearDue)) return "critical";
    if (hasIssues || (notChecked && parseInt(item.amezi_atwite) >= 6)) return "high";
    if (notChecked || teenMom || olderMom) return "medium";
    return "low";
  };

  const riskConfig = {
    critical: { color: theme.danger, label: "Bikomeye Cyane", icon: "exclamation-triangle-fill", animate: true },
    high: { color: theme.warning, label: "Bikomeye", icon: "exclamation-circle-fill" },
    medium: { color: theme.info, label: "Bisanzwe", icon: "info-circle-fill" },
    low: { color: theme.success, label: "Byiza", icon: "check-circle-fill" },
  };

  // ══════════════════════════════════════════════════════════════
  // DATA LOADING
  // ══════════════════════════════════════════════════════════════
  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_URL);
      const raw = res.data;
      setData(Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []));
    } catch {
      showAlert("Habaye ikosa mu gushaka amakuru", "danger");
    }
    setLoading(false);
  };

  // ══════════════════════════════════════════════════════════════
  // FILTERING & PAGINATION
  // ══════════════════════════════════════════════════════════════
  const filtered = useMemo(() => {
    let result = [...data];

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(item =>
        item.amazina?.toLowerCase().includes(term) ||
        item.aderesi?.toLowerCase().includes(term) ||
        item.nimero_ya_telephone?.includes(term) ||
        item.ivuriro?.toLowerCase().includes(term)
      );
    }

    if (filterTrimester !== "all") {
      result = result.filter(item => getTrimester(item.amezi_atwite) === parseInt(filterTrimester));
    }

    if (filterCheckup !== "all") {
      result = result.filter(item => item.asuzumwe_kuvuzi === filterCheckup);
    }

    if (filterRisk !== "all") {
      result = result.filter(item => getRiskLevel(item) === filterRisk);
    }

    return result;
  }, [data, searchTerm, filterTrimester, filterCheckup, filterRisk]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const currentItems = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, filterTrimester, filterCheckup, filterRisk]);

  // ══════════════════════════════════════════════════════════════
  // ALERT & VALIDATION
  // ══════════════════════════════════════════════════════════════
  const showAlert = (message, type = "success") => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: "", type: "" }), 4500);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.amazina.trim()) newErrors.amazina = "Amazina arakenewe";
    if (!form.imyaka || parseInt(form.imyaka) < 14 || parseInt(form.imyaka) > 50) {
      newErrors.imyaka = "Imyaka (14-50)";
    }
    if (!form.amezi_atwite || parseInt(form.amezi_atwite) < 1 || parseInt(form.amezi_atwite) > 9) {
      newErrors.amezi_atwite = "Amezi (1-9)";
    }
    if (!form.nimero_ya_telephone.trim()) {
      newErrors.nimero_ya_telephone = "Telephone irakenewe";
    } else if (!/^\d{10}$/.test(form.nimero_ya_telephone.replace(/\s/g, ""))) {
      newErrors.nimero_ya_telephone = "Imibare 10 gusa";
    }
    if (!form.aderesi.trim()) newErrors.aderesi = "Aderesi irakenewe";

    // Check for duplicate phone
    if (!editId && form.nimero_ya_telephone) {
      const exists = data.some(item => item.nimero_ya_telephone === form.nimero_ya_telephone);
      if (exists) newErrors.nimero_ya_telephone = "Numero isanzwe!";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ══════════════════════════════════════════════════════════════
  // CRUD OPERATIONS
  // ══════════════════════════════════════════════════════════════
  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`${API_URL}/${editId}`, form);
        showAlert("Byahinduwe neza!", "success");
      } else {
        await axios.post(API_URL, form);
        showAlert("Byongewe neza!", "success");
      }
      setShowModal(false);
      resetForm();
      loadData();
    } catch {
      showAlert("Habaye ikosa", "danger");
    }
    setSaving(false);
  };

  const handleInstantSave = async () => {
    const generated = getUniqueRandomData();
    setSaving(true);
    try {
      await axios.post(API_URL, generated);
      showAlert(`"${generated.amazina}" yongewe!`, "success");
      loadData();
    } catch {
      showAlert("Habaye ikosa", "danger");
    }
    setSaving(false);
  };

  const handleBatchSave = async (count = 5) => {
    setSaving(true);
    let success = 0;

    for (let i = 0; i < count; i++) {
      const generated = getUniqueRandomData();
      try {
        await axios.post(API_URL, generated);
        data.push(generated);
        success++;
      } catch {}
    }

    if (success > 0) {
      showAlert(`${success} yongewe neza!`, "success");
      loadData();
    } else {
      showAlert("Habaye ikosa", "danger");
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/${deleteId}`);
      showAlert("Byasibwe neza!", "success");
      loadData();
    } catch {
      showAlert("Gusiba byananiranye", "danger");
    }
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const handleEdit = (item) => {
    setForm({ ...item });
    setEditId(item.id);
    setErrors({});
    setShowModal(true);
  };

  const resetForm = () => {
    setForm({ ...emptyForm });
    setEditId(null);
    setErrors({});
  };

  const openNewForm = () => {
    const generated = getUniqueRandomData();
    setForm(generated);
    setEditId(null);
    setErrors({});
    setAutoFillKey(k => k + 1);
    setShowModal(true);
  };

  const regenerateForm = () => {
    const generated = getUniqueRandomData();
    setForm(generated);
    setErrors({});
    setAutoFillKey(k => k + 1);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const exportToCSV = () => {
    const headers = ["Amazina", "Imyaka", "Telephone", "Aderesi", "Amezi", "Igihembwe", "Isuzuma", "Ibibazo"];
    const rows = filtered.map(d => [
      d.amazina, d.imyaka, d.nimero_ya_telephone, d.aderesi,
      d.amezi_atwite, `Igihembwe ${getTrimester(d.amezi_atwite)}`,
      d.asuzumwe_kuvuzi, d.ibibazo_byihariye || "Ntabyo"
    ]);
    const csvContent = [headers.join(","), ...rows.map(r => r.map(c => `"${c}"`).join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "abagore_batwite.csv";
    a.click();
  };

  // ══════════════════════════════════════════════════════════════
  // RENDER COMPONENTS
  // ══════════════════════════════════════════════════════════════
  const RiskBadge = ({ item }) => {
    const risk = getRiskLevel(item);
    const config = riskConfig[risk];
    return (
      <span
        className={`risk-badge ${config.animate ? "pulse" : ""}`}
        style={{ background: `${config.color}20`, color: config.color, border: `1px solid ${config.color}40` }}
      >
        <i className={`bi bi-${config.icon} me-1`}></i>
        {config.label}
      </span>
    );
  };

  const TrimesterBadge = ({ months }) => {
    const info = getTrimesterInfo(months);
    return (
      <div className="trimester-badge">
        <span className="trimester-label" style={{ color: info.color }}>{info.label}</span>
        <div className="trimester-progress">
          <div className="trimester-bar" style={{ width: `${info.progress}%`, background: info.color }}></div>
        </div>
        <span className="trimester-months">{months} amezi</span>
      </div>
    );
  };

  const StatCard = ({ icon, label, value, color, subtitle, urgent, onClick }) => (
    <div
      className={`stat-card ${urgent ? "urgent" : ""} ${onClick ? "clickable" : ""}`}
      style={{ "--accent": color }}
      onClick={onClick}
    >
      <div className="stat-icon" style={{ background: `${color}15`, color }}>
        <i className={`bi bi-${icon}`}></i>
      </div>
      <div className="stat-content">
        <div className="stat-value">{value}</div>
        <div className="stat-label">{label}</div>
        {subtitle && <div className="stat-subtitle">{subtitle}</div>}
      </div>
    </div>
  );

  const MobileCard = ({ item }) => {
    const risk = getRiskLevel(item);
    const trimesterInfo = getTrimesterInfo(item.amezi_atwite);
    const isExpanded = expandedCard === item.id;

    return (
      <div className={`mobile-card ${risk === "critical" ? "critical" : risk === "high" ? "high-risk" : ""}`}>
        <div className="card-header" onClick={() => setExpandedCard(isExpanded ? null : item.id)}>
          <div className="card-main">
            <div className="card-name-row">
              <h4 className="card-name">{item.amazina}</h4>
              <span className="card-age">{item.imyaka} imyaka</span>
            </div>
            <div className="card-pregnancy-info">
              <div className="pregnancy-progress">
                <div className="progress-track">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${(parseInt(item.amezi_atwite) / 9) * 100}%`,
                      background: trimesterInfo.color
                    }}
                  ></div>
                </div>
                <span className="progress-label">{item.amezi_atwite}/9 amezi</span>
              </div>
            </div>
          </div>
          <div className="card-status">
            <RiskBadge item={item} />
          </div>
        </div>

        <div className={`card-body ${isExpanded ? "expanded" : ""}`}>
          <div className="card-info">
            <div className="info-row">
              <i className="bi bi-geo-alt"></i>
              <span>{item.aderesi}</span>
            </div>
            <div className="info-row">
              <i className="bi bi-telephone"></i>
              <a href={`tel:${item.nimero_ya_telephone}`}>{item.nimero_ya_telephone}</a>
            </div>
            <div className="info-row tags">
              <span className={`tag ${item.asuzumwe_kuvuzi === "Yego" ? "success" : "warning"}`}>
                <i className={`bi bi-${item.asuzumwe_kuvuzi === "Yego" ? "check-circle" : "x-circle"} me-1`}></i>
                {item.asuzumwe_kuvuzi === "Yego" ? "Yasuzumwe" : "Ntiyasuzumwe"}
              </span>
              <span className="tag trimester" style={{ background: `${trimesterInfo.color}20`, color: trimesterInfo.color }}>
                {trimesterInfo.label}
              </span>
            </div>
            {item.ibibazo_byihariye && (
              <div className="info-row alert-row">
                <i className="bi bi-exclamation-triangle"></i>
                <span>{item.ibibazo_byihariye}</span>
              </div>
            )}
            {item.ivuriro && (
              <div className="info-row">
                <i className="bi bi-hospital"></i>
                <span>{item.ivuriro}</span>
              </div>
            )}
          </div>

          <div className="card-actions">
            <button className="action-btn primary" onClick={() => { setSelectedPerson(item); setShowDetailModal(true); }}>
              <i className="bi bi-eye"></i> Reba
            </button>
            <button className="action-btn edit" onClick={() => handleEdit(item)}>
              <i className="bi bi-pencil"></i>
            </button>
            <button className="action-btn danger" onClick={() => { setDeleteId(item.id); setShowDeleteModal(true); }}>
              <i className="bi bi-trash"></i>
            </button>
          </div>
        </div>

        <div className="card-expand" onClick={() => setExpandedCard(isExpanded ? null : item.id)}>
          <i className={`bi bi-chevron-${isExpanded ? "up" : "down"}`}></i>
        </div>
      </div>
    );
  };

  // ══════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ══════════════════════════════════════════════════════════════
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@300;400;500;600;700;800;900&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }

        .app-container {
          font-family: 'Nunito Sans', -apple-system, BlinkMacSystemFont, sans-serif;
          background: ${theme.bg};
          min-height: 100vh;
          color: ${theme.text};
          -webkit-font-smoothing: antialiased;
        }

        .glass {
          background: ${theme.surface};
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid ${theme.border};
          border-radius: 20px;
        }

        /* Header */
        .app-header {
          padding: 24px;
          margin-bottom: 24px;
          background: linear-gradient(135deg, ${theme.surface} 0%, rgba(232, 121, 249, 0.05) 100%);
        }

        .header-content {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          align-items: center;
          gap: 20px;
        }

        .header-title-group {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .header-icon {
          width: 60px;
          height: 60px;
          border-radius: 18px;
          background: ${theme.gradient};
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.6rem;
          color: white;
          box-shadow: 0 8px 32px ${theme.primaryGlow};
        }

        .header-title {
          font-size: clamp(1.4rem, 4vw, 2rem);
          font-weight: 800;
          background: ${theme.gradient};
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .header-subtitle {
          color: ${theme.textMuted};
          font-size: 0.9rem;
          margin-top: 4px;
        }

        .header-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }

        /* Buttons */
        .btn-glow {
          padding: 12px 20px;
          border-radius: 12px;
          font-weight: 600;
          font-size: 0.9rem;
          border: none;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        .btn-primary {
          background: ${theme.gradient};
          color: white;
          box-shadow: 0 4px 20px ${theme.primaryGlow};
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px ${theme.primaryGlow};
        }

        .btn-ghost {
          background: transparent;
          color: ${theme.text};
          border: 1px solid ${theme.border};
        }

        .btn-ghost:hover {
          background: rgba(255,255,255,0.05);
          border-color: ${theme.primary};
          color: ${theme.primary};
        }

        .btn-success {
          background: ${theme.success};
          color: #000;
        }

        .btn-warning {
          background: ${theme.warning};
          color: #000;
        }

        /* Stats Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 14px;
          margin-bottom: 24px;
        }

        .stat-card {
          padding: 18px;
          border-radius: 16px;
          background: ${theme.surface};
          border: 1px solid ${theme.border};
          display: flex;
          align-items: center;
          gap: 14px;
          transition: all 0.3s ease;
        }

        .stat-card.clickable {
          cursor: pointer;
        }

        .stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(0,0,0,0.3);
          border-color: var(--accent);
        }

        .stat-card.urgent {
          border-color: ${theme.danger};
          animation: urgentPulse 2s infinite;
        }

        @keyframes urgentPulse {
          0%, 100% { box-shadow: 0 0 0 0 ${theme.dangerGlow}; }
          50% { box-shadow: 0 0 0 8px transparent; }
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.3rem;
          flex-shrink: 0;
        }

        .stat-value {
          font-size: 1.6rem;
          font-weight: 800;
          line-height: 1;
        }

        .stat-label {
          font-size: 0.8rem;
          color: ${theme.textMuted};
          margin-top: 4px;
        }

        .stat-subtitle {
          font-size: 0.7rem;
          color: ${theme.textMuted};
          opacity: 0.7;
        }

        /* Filters */
        .filters-bar {
          padding: 16px 20px;
          margin-bottom: 24px;
        }

        .filters-grid {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1fr auto;
          gap: 12px;
          align-items: center;
        }

        @media (max-width: 1200px) {
          .filters-grid {
            grid-template-columns: 1fr 1fr 1fr;
          }
        }

        @media (max-width: 768px) {
          .filters-grid {
            grid-template-columns: 1fr;
          }
        }

        .search-input {
          position: relative;
        }

        .search-input i {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          color: ${theme.textMuted};
        }

        .search-input input {
          width: 100%;
          padding: 14px 14px 14px 44px;
          background: rgba(0,0,0,0.3);
          border: 1px solid ${theme.border};
          border-radius: 12px;
          color: ${theme.text};
          font-size: 0.95rem;
          outline: none;
          transition: all 0.3s;
        }

        .search-input input:focus {
          border-color: ${theme.primary};
          box-shadow: 0 0 0 4px ${theme.primaryGlow};
        }

        .filter-select {
          padding: 14px;
          background: rgba(0,0,0,0.3);
          border: 1px solid ${theme.border};
          border-radius: 12px;
          color: ${theme.text};
          font-size: 0.9rem;
          cursor: pointer;
          outline: none;
        }

        .filter-select:focus {
          border-color: ${theme.primary};
        }

        .view-toggle {
          display: flex;
          background: rgba(0,0,0,0.3);
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid ${theme.border};
        }

        .view-toggle button {
          padding: 12px 16px;
          background: transparent;
          border: none;
          color: ${theme.textMuted};
          cursor: pointer;
          transition: all 0.2s;
        }

        .view-toggle button.active {
          background: ${theme.primary};
          color: #000;
        }

        /* Table */
        .data-table {
          overflow: hidden;
        }

        .table-scroll {
          overflow-x: auto;
        }

        .modern-table {
          width: 100%;
          border-collapse: collapse;
        }

        .modern-table th {
          padding: 16px;
          text-align: left;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: ${theme.textMuted};
          background: rgba(0,0,0,0.3);
          border-bottom: 1px solid ${theme.border};
          white-space: nowrap;
        }

        .modern-table td {
          padding: 16px;
          font-size: 0.9rem;
          border-bottom: 1px solid ${theme.border};
          vertical-align: middle;
        }

        .modern-table tbody tr {
          transition: background 0.2s;
        }

        .modern-table tbody tr:hover {
          background: rgba(255,255,255,0.03);
        }

        .table-name {
          font-weight: 600;
          max-width: 180px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .table-link {
          color: ${theme.primary};
          text-decoration: none;
        }

        .table-actions {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
        }

        .table-action {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          border: 1px solid ${theme.border};
          background: transparent;
          color: ${theme.textMuted};
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .table-action:hover {
          transform: translateY(-2px);
        }

        .table-action.edit:hover {
          border-color: ${theme.primary};
          color: ${theme.primary};
          background: ${theme.primaryGlow};
        }

        .table-action.delete:hover {
          border-color: ${theme.danger};
          color: ${theme.danger};
          background: ${theme.dangerGlow};
        }

        .table-action.view:hover {
          border-color: ${theme.info};
          color: ${theme.info};
          background: rgba(56, 189, 248, 0.15);
        }

        /* Risk Badge */
        .risk-badge {
          display: inline-flex;
          align-items: center;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 0.75rem;
          font-weight: 600;
          white-space: nowrap;
        }

        .risk-badge.pulse {
          animation: badgePulse 2s infinite;
        }

        @keyframes badgePulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }

        /* Trimester Badge */
        .trimester-badge {
          display: flex;
          flex-direction: column;
          gap: 4px;
          min-width: 100px;
        }

        .trimester-label {
          font-size: 0.75rem;
          font-weight: 600;
        }

        .trimester-progress {
          height: 4px;
          background: rgba(255,255,255,0.1);
          border-radius: 2px;
          overflow: hidden;
        }

        .trimester-bar {
          height: 100%;
          border-radius: 2px;
          transition: width 0.5s ease;
        }

        .trimester-months {
          font-size: 0.7rem;
          color: ${theme.textMuted};
        }

        /* Status Tag */
        .status-tag {
          display: inline-flex;
          align-items: center;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .status-tag.success {
          background: ${theme.successGlow};
          color: ${theme.success};
        }

        .status-tag.warning {
          background: ${theme.warningGlow};
          color: ${theme.warning};
        }

        .status-tag.muted {
          background: rgba(255,255,255,0.05);
          color: ${theme.textMuted};
        }

        /* Mobile Cards */
        .cards-container {
          display: none;
        }

        @media (max-width: 992px) {
          .table-container { display: none !important; }
          .cards-container { display: block !important; }
        }

        .mobile-card {
          background: ${theme.surface};
          border: 1px solid ${theme.border};
          border-radius: 16px;
          margin-bottom: 12px;
          overflow: hidden;
          transition: all 0.3s;
        }

        .mobile-card.critical {
          border-color: ${theme.danger};
          border-width: 2px;
          box-shadow: 0 0 20px ${theme.dangerGlow};
        }

        .mobile-card.high-risk {
          border-color: ${theme.warning};
          border-width: 2px;
        }

        .mobile-card .card-header {
          padding: 16px;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
          cursor: pointer;
        }

        .mobile-card .card-name-row {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 8px;
        }

        .mobile-card .card-name {
          font-size: 1rem;
          font-weight: 700;
        }

        .mobile-card .card-age {
          font-size: 0.8rem;
          color: ${theme.textMuted};
          background: rgba(255,255,255,0.05);
          padding: 2px 8px;
          border-radius: 4px;
        }

        .mobile-card .pregnancy-progress {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .mobile-card .progress-track {
          flex: 1;
          height: 6px;
          background: rgba(255,255,255,0.1);
          border-radius: 3px;
          overflow: hidden;
        }

        .mobile-card .progress-fill {
          height: 100%;
          border-radius: 3px;
          transition: width 0.5s ease;
        }

        .mobile-card .progress-label {
          font-size: 0.8rem;
          color: ${theme.textMuted};
          white-space: nowrap;
        }

        .mobile-card .card-body {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.3s ease;
        }

        .mobile-card .card-body.expanded {
          max-height: 500px;
        }

        .mobile-card .card-info {
          padding: 0 16px 16px;
        }

        .mobile-card .info-row {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 0;
          font-size: 0.9rem;
          color: ${theme.textMuted};
          border-bottom: 1px solid ${theme.border};
        }

        .mobile-card .info-row:last-child {
          border-bottom: none;
        }

        .mobile-card .info-row i {
          color: ${theme.primary};
          width: 20px;
        }

        .mobile-card .info-row a {
          color: ${theme.primary};
          text-decoration: none;
        }

        .mobile-card .info-row.tags {
          gap: 8px;
          flex-wrap: wrap;
        }

        .mobile-card .tag {
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .mobile-card .tag.success {
          background: ${theme.successGlow};
          color: ${theme.success};
        }

        .mobile-card .tag.warning {
          background: ${theme.warningGlow};
          color: ${theme.warning};
        }

        .mobile-card .info-row.alert-row {
          background: ${theme.dangerGlow};
          border-radius: 8px;
          padding: 10px;
          color: ${theme.danger};
          border: none;
          margin-top: 8px;
        }

        .mobile-card .info-row.alert-row i {
          color: ${theme.danger};
        }

        .mobile-card .card-actions {
          display: flex;
          gap: 8px;
          padding: 12px 16px;
          border-top: 1px solid ${theme.border};
        }

        .mobile-card .action-btn {
          flex: 1;
          padding: 10px;
          border-radius: 10px;
          border: none;
          font-weight: 600;
          font-size: 0.85rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          transition: all 0.2s;
        }

        .mobile-card .action-btn.primary {
          background: ${theme.gradient};
          color: white;
        }

        .mobile-card .action-btn.edit {
          background: rgba(255,255,255,0.1);
          color: ${theme.primary};
        }

        .mobile-card .action-btn.danger {
          background: rgba(255,255,255,0.1);
          color: ${theme.danger};
        }

        .mobile-card .card-expand {
          text-align: center;
          padding: 8px;
          color: ${theme.textMuted};
          font-size: 0.8rem;
          cursor: pointer;
        }

        /* Pagination */
        .pagination-container {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 8px;
          margin-top: 24px;
          flex-wrap: wrap;
        }

        .page-btn {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          border: 1px solid ${theme.border};
          background: transparent;
          color: ${theme.textMuted};
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          transition: all 0.2s;
        }

        .page-btn:hover:not(:disabled) {
          border-color: ${theme.primary};
          color: ${theme.primary};
        }

        .page-btn.active {
          background: ${theme.gradient};
          color: white;
          border-color: transparent;
        }

        .page-btn:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        /* Empty State */
        .empty-state {
          text-align: center;
          padding: 60px 20px;
        }

        .empty-icon {
          font-size: 4rem;
          color: ${theme.textMuted};
          opacity: 0.3;
          margin-bottom: 16px;
        }

        /* Loading */
        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
        }

        .loading-spinner {
          width: 48px;
          height: 48px;
          border: 3px solid ${theme.border};
          border-top-color: ${theme.primary};
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Modal Styles */
        .modal-content {
          background: ${theme.surface} !important;
          border: 1px solid ${theme.border} !important;
          border-radius: 20px !important;
          color: ${theme.text} !important;
        }

        .modal-header {
          border-bottom: 1px solid ${theme.border} !important;
          padding: 20px 24px !important;
        }

        .modal-body {
          padding: 24px !important;
        }

        .modal-footer {
          border-top: 1px solid ${theme.border} !important;
          padding: 16px 24px !important;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-label {
          display: block;
          font-size: 0.85rem;
          font-weight: 600;
          color: ${theme.textMuted};
          margin-bottom: 8px;
        }

        .form-input, .form-select, .form-textarea {
          width: 100%;
          padding: 14px 16px;
          background: rgba(0,0,0,0.3);
          border: 1px solid ${theme.border};
          border-radius: 12px;
          color: ${theme.text};
          font-size: 0.95rem;
          outline: none;
          transition: all 0.3s;
        }

        .form-textarea {
          min-height: 80px;
          resize: vertical;
        }

        .form-input:focus, .form-select:focus, .form-textarea:focus {
          border-color: ${theme.primary};
          box-shadow: 0 0 0 4px ${theme.primaryGlow};
        }

        .form-input.error {
          border-color: ${theme.danger};
        }

        .form-error {
          color: ${theme.danger};
          font-size: 0.8rem;
          margin-top: 6px;
        }

        .form-input.filled {
          border-left: 3px solid ${theme.success};
        }

        /* Auto-fill Banner */
        .autofill-banner {
          background: linear-gradient(135deg, ${theme.primaryGlow}, rgba(129, 140, 248, 0.1));
          border: 1px solid ${theme.primary}40;
          border-radius: 14px;
          padding: 16px;
          margin-bottom: 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
        }

        .autofill-btn {
          padding: 10px 16px;
          background: transparent;
          border: 1px solid ${theme.primary};
          border-radius: 10px;
          color: ${theme.primary};
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.3s;
          white-space: nowrap;
        }

        .autofill-btn:hover {
          background: ${theme.primaryGlow};
        }

        /* Detail Modal */
        .detail-header {
          text-align: center;
          margin-bottom: 24px;
        }

        .detail-avatar {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: ${theme.gradient};
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          color: white;
          margin: 0 auto 16px;
        }

        .detail-name {
          font-size: 1.4rem;
          font-weight: 700;
          margin-bottom: 8px;
        }

        .detail-pregnancy-bar {
          background: rgba(255,255,255,0.1);
          height: 10px;
          border-radius: 5px;
          overflow: hidden;
          margin: 16px 0;
        }

        .detail-pregnancy-fill {
          height: 100%;
          border-radius: 5px;
          transition: width 0.5s ease;
        }

        .detail-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }

        .detail-item {
          background: rgba(0,0,0,0.2);
          padding: 16px;
          border-radius: 12px;
        }

        .detail-item-label {
          font-size: 0.75rem;
          color: ${theme.textMuted};
          text-transform: uppercase;
          margin-bottom: 6px;
        }

        .detail-item-value {
          font-size: 1rem;
          font-weight: 600;
        }

        .detail-alert {
          background: ${theme.dangerGlow};
          border: 1px solid ${theme.danger}40;
          border-radius: 12px;
          padding: 16px;
          margin-top: 16px;
          color: ${theme.danger};
        }

        /* Alert Toast */
        .alert-toast {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 9999;
          max-width: 400px;
          animation: slideIn 0.4s ease;
        }

        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        /* Responsive */
        @media (max-width: 768px) {
          .header-content {
            flex-direction: column;
            align-items: flex-start;
          }

          .header-actions {
            width: 100%;
          }

          .header-actions .btn-glow {
            flex: 1;
            justify-content: center;
          }

          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .detail-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 480px) {
          .header-actions {
            flex-direction: column;
          }

          .header-actions .btn-glow {
            width: 100%;
          }
        }
      `}</style>

      <div className="app-container">
        <Container fluid className="py-4 px-3 px-md-4">

          {/* Alert Toast */}
          {alert.show && (
            <div className="alert-toast">
              <Alert variant={alert.type} onClose={() => setAlert({ show: false })} dismissible className="glass border-0 shadow-lg">
                <i className={`bi bi-${alert.type === "success" ? "check-circle-fill" : "exclamation-triangle-fill"} me-2`}></i>
                {alert.message}
              </Alert>
            </div>
          )}

          {/* Header */}
          <div className="glass app-header">
            <div className="header-content">
              <div className="header-title-group">
                <div className="header-icon">
                  <i className="bi bi-heart-pulse-fill"></i>
                </div>
                <div>
                  <h1 className="header-title">Abagore Batwite</h1>
                  <p className="header-subtitle">Gukurikirana imibereho y'abagore batwite mu mudugudu</p>
                </div>
              </div>

              <div className="header-actions">
                <button className="btn-glow btn-ghost" onClick={loadData} disabled={loading}>
                  <i className="bi bi-arrow-repeat"></i>
                  <span className="d-none d-sm-inline">Refresh</span>
                </button>
                <button className="btn-glow btn-ghost" onClick={exportToCSV} disabled={!filtered.length}>
                  <i className="bi bi-download"></i>
                  <span className="d-none d-sm-inline">Export</span>
                </button>
                <button className="btn-glow btn-success" onClick={handleInstantSave} disabled={saving}>
                  <i className="bi bi-lightning-fill"></i>
                  <span className="d-none d-sm-inline">Bika 1</span>
                </button>
                <button className="btn-glow btn-warning" onClick={() => handleBatchSave(5)} disabled={saving}>
                  <i className="bi bi-lightning-charge-fill"></i>
                  <span className="d-none d-sm-inline">Bika 5</span>
                </button>
                <button className="btn-glow btn-primary" onClick={openNewForm}>
                  <i className="bi bi-plus-lg"></i>
                  <span>Ongeraho</span>
                </button>
              </div>
            </div>
          </div>

          {/* High Risk Alert */}
          {statistics.highRisk > 0 && (
            <Alert variant="danger" className="glass border-danger border-2 mb-4">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <strong>Icyitonderwa:</strong> Hari abagore <strong>{statistics.highRisk}</strong> bafite ibibazo byihariye bakeneye kwitabwaho!
            </Alert>
          )}

          {/* Due This Month Alert */}
          {statistics.dueThisMonth > 0 && (
            <Alert variant="warning" className="glass border-warning mb-4">
              <i className="bi bi-clock-fill me-2"></i>
              <strong>Abibutsa:</strong> Abagore <strong>{statistics.dueThisMonth}</strong> bari hafi kubyara (amezi 8-9)!
            </Alert>
          )}

          {/* Stats Grid */}
          <div className="stats-grid">
            <StatCard
              icon="people-fill"
              label="Bose Hamwe"
              value={statistics.total}
              color={theme.primary}
            />
            <StatCard
              icon="check-circle-fill"
              label="Basuzumwe"
              value={statistics.checkedUp}
              color={theme.success}
              subtitle={`${statistics.total > 0 ? Math.round((statistics.checkedUp / statistics.total) * 100) : 0}%`}
              onClick={() => setFilterCheckup("Yego")}
            />
            <StatCard
              icon="x-circle-fill"
              label="Batasuzumwe"
              value={statistics.notChecked}
              color={theme.warning}
              onClick={() => setFilterCheckup("Oya")}
            />
            <StatCard
              icon="exclamation-triangle-fill"
              label="Bafite Ibibazo"
              value={statistics.highRisk}
              color={theme.danger}
              urgent={statistics.highRisk > 0}
              onClick={() => setFilterRisk("high")}
            />
            <StatCard
              icon="calendar-event"
              label="Igihembwe 1"
              value={statistics.trimester1}
              color={theme.info}
              onClick={() => setFilterTrimester("1")}
            />
            <StatCard
              icon="calendar2-week"
              label="Igihembwe 2"
              value={statistics.trimester2}
              color={theme.secondary}
              onClick={() => setFilterTrimester("2")}
            />
            <StatCard
              icon="calendar-check"
              label="Igihembwe 3"
              value={statistics.trimester3}
              color={theme.danger}
              onClick={() => setFilterTrimester("3")}
            />
            <StatCard
              icon="clock-history"
              label="Bari hafi"
              value={statistics.dueThisMonth}
              color={theme.warning}
              urgent={statistics.dueThisMonth > 0}
            />
          </div>

          {/* Filters */}
          <div className="glass filters-bar">
            <div className="filters-grid">
              <div className="search-input">
                <i className="bi bi-search"></i>
                <input
                  type="text"
                  placeholder="Shakisha amazina, aderesi, telephone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <select className="filter-select" value={filterTrimester} onChange={(e) => setFilterTrimester(e.target.value)}>
                <option value="all">Ibihembwe Byose</option>
                <option value="1">Igihembwe 1</option>
                <option value="2">Igihembwe 2</option>
                <option value="3">Igihembwe 3</option>
              </select>

              <select className="filter-select" value={filterCheckup} onChange={(e) => setFilterCheckup(e.target.value)}>
                <option value="all">Isuzuma Byose</option>
                <option value="Yego">Basuzumwe</option>
                <option value="Oya">Batasuzumwe</option>
              </select>

              <select className="filter-select" value={filterRisk} onChange={(e) => setFilterRisk(e.target.value)}>
                <option value="all">Urwego Byose</option>
                <option value="critical">Bikomeye Cyane</option>
                <option value="high">Bikomeye</option>
                <option value="medium">Bisanzwe</option>
                <option value="low">Byiza</option>
              </select>

              <div className="view-toggle d-none d-lg-flex">
                <button className={viewMode === "table" ? "active" : ""} onClick={() => setViewMode("table")}>
                  <i className="bi bi-table"></i>
                </button>
                <button className={viewMode === "card" ? "active" : ""} onClick={() => setViewMode("card")}>
                  <i className="bi bi-grid"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Data Display */}
          {loading ? (
            <div className="glass">
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p className="mt-3" style={{ color: theme.textMuted }}>Tegereza gato...</p>
              </div>
            </div>
          ) : currentItems.length === 0 ? (
            <div className="glass">
              <div className="empty-state">
                <i className="bi bi-inbox empty-icon"></i>
                <h3>Nta makuru abonetse</h3>
                <p style={{ color: theme.textMuted }}>Tangira wongeremo amakuru cyangwa uhindure filters.</p>
                <button className="btn-glow btn-primary mt-3" onClick={() => handleBatchSave(5)}>
                  <i className="bi bi-lightning-charge-fill me-2"></i>
                  Ongeraho 5 mu buryo bwikora
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="glass data-table table-container" style={{ display: viewMode === "table" ? "block" : "none" }}>
                <div className="table-scroll">
                  <table className="modern-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Amazina</th>
                        <th>Imyaka</th>
                        <th>Telephone</th>
                        <th>Igihembwe</th>
                        <th>Isuzuma</th>
                        <th>Urwego</th>
                         <th className="text-right">Ibikorwa</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.map((item, idx) => (
                        <tr key={item.id}>
                          <td>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                          <td className="table-name">{item.amazina}</td>
                          <td>{item.imyaka}</td>
                          <td>
                            <a href={`tel:${item.nimero_ya_telephone}`} className="table-link">
                              {item.nimero_ya_telephone}
                            </a>
                          </td>
                          <td>
                            <TrimesterBadge months={item.amezi_atwite} />
                          </td>
                          <td>
                            <span className={`status-tag ${item.asuzumwe_kuvuzi === "Yego" ? "success" : "warning"}`}>
                              {item.asuzumwe_kuvuzi}
                            </span>
                          </td>
                          <td>
                            <RiskBadge item={item} />
                          </td>
                          <td>
                            <div className="table-actions">
                              <button className="table-action view" onClick={() => { setSelectedPerson(item); setShowDetailModal(true); }}>
                                <i className="bi bi-eye"></i>
                              </button>
                              <button className="table-action edit" onClick={() => handleEdit(item)}>
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button className="table-action delete" onClick={() => { setDeleteId(item.id); setShowDeleteModal(true); }}>
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Card View */}
              {viewMode === "card" && (
                <Row className="g-3 d-none d-lg-flex">
                  {currentItems.map((item) => (
                    <Col lg={4} key={item.id}>
                      <MobileCard item={item} />
                    </Col>
                  ))}
                </Row>
              )}

              {/* Mobile Cards */}
              <div className="cards-container">
                {currentItems.map((item) => (
                  <MobileCard key={item.id} item={item} />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-container">
                  <button className="page-btn" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                  <button className="page-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                    <i className="bi bi-chevron-left"></i>
                  </button>

                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    let page;
                    if (totalPages <= 5) {
                      page = i + 1;
                    } else if (currentPage <= 3) {
                      page = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      page = totalPages - 4 + i;
                    } else {
                      page = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={page}
                        className={`page-btn ${page === currentPage ? "active" : ""}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    );
                  })}

                  <button className="page-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                    <i className="bi bi-chevron-right"></i>
                  </button>
                  <button className="page-btn" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </Container>

        {/* ═══════════════════════════════════════════════════════════
            MODALS
        ═══════════════════════════════════════════════════════════ */}

        {/* Add/Edit Modal */}
        <Modal show={showModal} onHide={() => { setShowModal(false); resetForm(); }} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>
              <i className={`bi bi-${editId ? "pencil-square" : "plus-circle"} me-2`} style={{ color: theme.primary }}></i>
              {editId ? "Hindura Amakuru" : "Ongeraho Umugore Utwite"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* Auto-fill Banner */}
            {!editId && (
              <div className="autofill-banner" key={autoFillKey}>
                <div className="d-flex align-items-center gap-3">
                  <i className="bi bi-lightning-fill" style={{ fontSize: "1.5rem", color: theme.warning }}></i>
                  <div>
                    <strong>Amakuru yuzuzanyije!</strong>
                    <p className="mb-0 small" style={{ color: theme.textMuted }}>Ushobora kuyahindura cyangwa gusubiramo.</p>
                  </div>
                </div>
                <button className="autofill-btn" onClick={regenerateForm}>
                  <i className="bi bi-arrow-repeat"></i>
                  Uzuza Bundi
                </button>
              </div>
            )}

            <Form>
              <Row>
                <Col md={6}>
                  <div className="form-group">
                    <label className="form-label">Amazina</label>
                    <input
                      type="text"
                      className={`form-input ${form.amazina ? "filled" : ""} ${errors.amazina ? "error" : ""}`}
                      name="amazina"
                      value={form.amazina}
                      onChange={handleChange}
                      placeholder="Amazina yose..."
                    />
                    {errors.amazina && <div className="form-error">{errors.amazina}</div>}
                  </div>
                </Col>
                <Col md={3}>
                  <div className="form-group">
                    <label className="form-label">Imyaka</label>
                    <input
                      type="number"
                      className={`form-input ${form.imyaka ? "filled" : ""} ${errors.imyaka ? "error" : ""}`}
                      name="imyaka"
                      value={form.imyaka}
                      onChange={handleChange}
                      placeholder="14-50"
                    />
                    {errors.imyaka && <div className="form-error">{errors.imyaka}</div>}
                  </div>
                </Col>
                <Col md={3}>
                  <div className="form-group">
                    <label className="form-label">Amezi Atwite</label>
                    <input
                      type="number"
                      className={`form-input ${form.amezi_atwite ? "filled" : ""} ${errors.amezi_atwite ? "error" : ""}`}
                      name="amezi_atwite"
                      value={form.amezi_atwite}
                      onChange={handleChange}
                      placeholder="1-9"
                      min="1"
                      max="9"
                    />
                    {errors.amezi_atwite && <div className="form-error">{errors.amezi_atwite}</div>}
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <div className="form-group">
                    <label className="form-label">Telephone</label>
                    <input
                      type="text"
                      className={`form-input ${form.nimero_ya_telephone ? "filled" : ""} ${errors.nimero_ya_telephone ? "error" : ""}`}
                      name="nimero_ya_telephone"
                      value={form.nimero_ya_telephone}
                      onChange={handleChange}
                      placeholder="07XXXXXXXX"
                    />
                    {errors.nimero_ya_telephone && <div className="form-error">{errors.nimero_ya_telephone}</div>}
                  </div>
                </Col>
                <Col md={6}>
                  <div className="form-group">
                    <label className="form-label">Aderesi</label>
                    <input
                      type="text"
                      className={`form-input ${form.aderesi ? "filled" : ""} ${errors.aderesi ? "error" : ""}`}
                      name="aderesi"
                      value={form.aderesi}
                      onChange={handleChange}
                      placeholder="Aderesi..."
                    />
                    {errors.aderesi && <div className="form-error">{errors.aderesi}</div>}
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <div className="form-group">
                    <label className="form-label">Yasuzumwe ku ivuriro?</label>
                    <select
                      className="form-select"
                      name="asuzumwe_kuvuzi"
                      value={form.asuzumwe_kuvuzi}
                      onChange={handleChange}
                    >
                      <option value="Oya">Oya</option>
                      <option value="Yego">Yego</option>
                    </select>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="form-group">
                    <label className="form-label">Ivuriro</label>
                    <input
                      type="text"
                      className={`form-input ${form.ivuriro ? "filled" : ""}`}
                      name="ivuriro"
                      value={form.ivuriro}
                      onChange={handleChange}
                      placeholder="Izina ry'ivuriro..."
                    />
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <div className="form-group">
                    <label className="form-label">Ibibazo byihariye</label>
                    <textarea
                      className={`form-textarea ${form.ibibazo_byihariye ? "filled" : ""}`}
                      name="ibibazo_byihariye"
                      value={form.ibibazo_byihariye}
                      onChange={handleChange}
                      placeholder="Ibibazo by'ubuzima bifitanye isano no gutwita..."
                    ></textarea>
                  </div>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => { setShowModal(false); resetForm(); }}>
              Bireke
            </Button>
            {!editId && (
              <Button variant="outline-warning" onClick={regenerateForm}>
                <i className="bi bi-arrow-repeat me-2"></i>
                Uzuza Bundi
              </Button>
            )}
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? <Spinner size="sm" className="me-2" /> : <i className={`bi bi-${editId ? "check-lg" : "save"} me-2`}></i>}
              {editId ? "Hindura" : "Bika"}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Delete Modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered size="sm">
          <Modal.Body className="text-center py-4">
            <div className="mb-3">
              <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: "3rem", color: theme.danger }}></i>
            </div>
            <h5 className="fw-bold mb-2">Emeza Gusiba</h5>
            <p style={{ color: theme.textMuted }}>Urashaka koko gusiba aya makuru? Ntibizagaruka.</p>
            <div className="d-flex gap-2 justify-content-center mt-4">
              <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Bireke</Button>
              <Button variant="danger" onClick={handleDelete}>
                <i className="bi bi-trash me-2"></i>Siba
              </Button>
            </div>
          </Modal.Body>
        </Modal>

        {/* Detail Modal */}
        <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="bi bi-person-lines-fill me-2" style={{ color: theme.primary }}></i>
              Amakuru Arambuye
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedPerson && (
              <>
                <div className="detail-header">
                  <div className="detail-avatar">
                    <i className="bi bi-person-heart"></i>
                  </div>
                  <h3 className="detail-name">{selectedPerson.amazina}</h3>
                  <div className="d-flex justify-content-center gap-2 flex-wrap">
                    <Badge bg="secondary">{selectedPerson.imyaka} imyaka</Badge>
                    <RiskBadge item={selectedPerson} />
                  </div>

                  <div className="detail-pregnancy-bar">
                    <div
                      className="detail-pregnancy-fill"
                      style={{
                        width: `${(parseInt(selectedPerson.amezi_atwite) / 9) * 100}%`,
                        background: getTrimesterInfo(selectedPerson.amezi_atwite).color
                      }}
                    ></div>
                  </div>
                  <p style={{ color: theme.textMuted }}>
                    {selectedPerson.amezi_atwite} amezi / 9 — {getTrimesterInfo(selectedPerson.amezi_atwite).label}
                  </p>
                </div>

                <div className="detail-grid">
                  <div className="detail-item">
                    <div className="detail-item-label">Telephone</div>
                    <div className="detail-item-value">
                      <a href={`tel:${selectedPerson.nimero_ya_telephone}`} style={{ color: theme.primary }}>
                        {selectedPerson.nimero_ya_telephone}
                      </a>
                    </div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-item-label">Aderesi</div>
                    <div className="detail-item-value">{selectedPerson.aderesi}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-item-label">Isuzuma</div>
                    <div className="detail-item-value">
                      <span style={{ color: selectedPerson.asuzumwe_kuvuzi === "Yego" ? theme.success : theme.warning }}>
                        {selectedPerson.asuzumwe_kuvuzi === "Yego" ? "✓ Yasuzumwe" : "✗ Ntiyasuzumwe"}
                      </span>
                    </div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-item-label">Ivuriro</div>
                    <div className="detail-item-value">{selectedPerson.ivuriro || "—"}</div>
                  </div>
                </div>

                {selectedPerson.ibibazo_byihariye && (
                  <div className="detail-alert">
                    <strong><i className="bi bi-exclamation-triangle me-2"></i>Ibibazo byihariye:</strong>
                    <p className="mb-0 mt-2">{selectedPerson.ibibazo_byihariye}</p>
                  </div>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDetailModal(false)}>Funga</Button>
            <Button variant="primary" onClick={() => { setShowDetailModal(false); handleEdit(selectedPerson); }}>
              <i className="bi bi-pencil me-2"></i>Hindura
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
}

export default AbagoreBatwite;